import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import './Home.css';

const problemPoints = [
  {
    icon: 'mountain' as const,
    title: 'Geography isolates communities',
    text: 'Hills, forests, and trekking routes sit beyond reliable cellular coverage. People travel through dead zones every day.',
  },
  {
    icon: 'flame' as const,
    title: 'Disasters damage infrastructure',
    text: 'Landslides, floods, and earthquakes knock out telecom towers exactly when communication matters most.',
  },
  {
    icon: 'phone-off' as const,
    title: 'Emergency numbers need signal',
    text: 'Even calling 100 is impossible when the device cannot reach a tower. The signal is the system, and the system failed.',
  },
  {
    icon: 'clock' as const,
    title: 'Information arrives too late',
    text: 'Fragmented channels and no centralized view delay response. Minutes lost become lives lost.',
  },
];

const flowSteps = [
  { icon: 'sos' as const, label: 'SOS device', note: 'One-touch button, GPS, battery' },
  { icon: 'lora' as const, label: 'LoRa network', note: 'Long-range, low-power radio' },
  { icon: 'gateway' as const, label: 'Setu gateway', note: 'Receives & forwards packets' },
  { icon: 'cloud' as const, label: 'Setu platform', note: 'Real-time dashboard' },
  { icon: 'responder' as const, label: 'Emergency team', note: 'Acknowledge, assign, dispatch' },
];

const principles = [
  { icon: 'zap' as const, title: 'Resilience first', text: 'Designed to function when cellular, internet, and power fail.' },
  { icon: 'heart' as const, title: 'People over tech', text: 'Simple enough to use under stress. No app to open, no number to remember.' },
  { icon: 'globe' as const, title: 'Built for Nepal', text: 'Engineering decisions grounded in the realities of remote Nepali geography.' },
  { icon: 'spark' as const, title: 'Modular & open', text: 'Components can be upgraded independently. Vendor-neutral hardware.' },
];

const stats = [
  { value: '15+ km', label: 'Typical LoRa range' },
  { value: '24/7', label: 'Standby on battery' },
  { value: '<3 s', label: 'SOS to dashboard' },
  { value: '0 SIM', label: 'cards required' },
];

export default function Home() {
  return (
    <>
      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="hero">
        <div className="hero__bg" aria-hidden>
          <svg className="hero__map" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
            <defs>
              <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#d8232a" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#d8232a" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="terrain" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#1a2849" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#1a2849" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {/* mountain silhouettes */}
            <path
              d="M0 480 L120 380 L220 430 L320 320 L420 400 L520 280 L640 360 L760 240 L880 340 L1000 290 L1120 360 L1200 320 L1200 600 L0 600 Z"
              fill="url(#terrain)"
            />
            <path
              d="M0 540 L100 470 L200 510 L300 440 L420 500 L540 430 L660 490 L780 440 L900 500 L1020 460 L1140 510 L1200 480 L1200 600 L0 600 Z"
              fill="#1a2849"
              fillOpacity="0.06"
            />
            {/* signal arcs emanating from device */}
            <g className="hero__pulses">
              <circle cx="300" cy="420" r="14" fill="url(#pinGlow)" />
              <circle cx="300" cy="420" r="32" fill="none" stroke="#d8232a" strokeWidth="1.5" strokeOpacity="0.4" />
              <circle cx="300" cy="420" r="60" fill="none" stroke="#d8232a" strokeWidth="1" strokeOpacity="0.25" />
              <circle cx="300" cy="420" r="100" fill="none" stroke="#d8232a" strokeWidth="0.8" strokeOpacity="0.15" />
            </g>
            {/* gateway location */}
            <g>
              <circle cx="780" cy="280" r="6" fill="#1a2849" />
              <circle cx="780" cy="280" r="12" fill="none" stroke="#1a2849" strokeWidth="1.5" strokeOpacity="0.4" />
            </g>
            {/* path from device to gateway */}
            <path
              d="M300 420 Q 540 360 780 280"
              stroke="#d8232a"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              fill="none"
              strokeOpacity="0.6"
            />
          </svg>
        </div>

        <div className="container hero__inner">
          <div className="hero__content">
            <span className="hero__pill">
              <span className="hero__pill-dot" />
              Built for Nepal's emergency reality
            </span>

            <h1 className="hero__title">
              Connecting lives
              <br />
              <span className="hero__title-accent">when networks fail.</span>
            </h1>

            <p className="hero__lede">
              SETU is a low-cost, resilient emergency communication and response system. When
              landslides, floods, or power outages silence the network, SETU keeps the bridge
              open, so that help can still be called, located, and dispatched.
            </p>

            <div className="hero__cta">
              <Link to="/technology" className="btn btn--primary btn--lg">
                See how it works
                <Icon name="arrow-right" size={18} className="arrow" />
              </Link>
              <Link to="/dashboard" className="btn btn--ghost btn--lg">
                <Icon name="play" size={16} />
                View live dashboard
              </Link>
            </div>

            <ul className="hero__stats">
              {stats.map((s) => (
                <li key={s.label}>
                  <strong>{s.value}</strong>
                  <span>{s.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="hero__device" aria-hidden>
            <DeviceMock />
          </div>
        </div>
      </section>

      {/* ============================================================
          THE PROBLEM
          ============================================================ */}
      <section className="section section--soft">
        <div className="container">
          <div className="section__head">
            <div className="reveal">
              <span className="eyebrow">The Problem</span>
              <h2>When communication is needed most, the network disappears.</h2>
            </div>
            <p className="lede reveal reveal-delay-1">
              Nepal's mountains, monsoon rains, and remote settlements make conventional
              communication fragile. A single damaged tower or power failure can silence an entire
              valley, precisely when people need to call for help.
            </p>
          </div>

          <div className="grid grid-2 problem-grid">
            {problemPoints.map((p, i) => (
              <div key={p.title} className={`card problem-card reveal reveal-delay-${i + 1}`}>
                <div className="problem-card__icon">
                  <Icon name={p.icon} size={22} />
                </div>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          SOLUTION: Flow
          ============================================================ */}
      <section className="section">
        <div className="container">
          <div className="section__head section__head--center">
            <div className="reveal" style={{ textAlign: 'center' }}>
              <span className="eyebrow">The Setu Solution</span>
              <h2>An independent communication bridge, built for the moment of crisis.</h2>
            </div>
            <p className="lede reveal reveal-delay-1" style={{ textAlign: 'center', margin: '0 auto' }}>
              SETU creates an alternative pathway using LoRa long-range wireless. A physical SOS
              becomes a routed packet, becomes a dashboard alert, becomes a dispatched response, all without depending on the cellular network.
              all without depending on the cellular network.
            </p>
          </div>

          <div className="flow reveal reveal-delay-2">
            {flowSteps.map((s, i) => (
              <div key={s.label} className="flow__step">
                <div className="flow__node">
                  <Icon name={s.icon} size={28} />
                </div>
                <div className="flow__text">
                  <strong>{s.label}</strong>
                  <span>{s.note}</span>
                </div>
                {i < flowSteps.length - 1 && <div className="flow__connector" aria-hidden />}
              </div>
            ))}
          </div>

          <div className="cta-row reveal reveal-delay-3">
            <Link to="/technology" className="btn btn--secondary">
              Explore the architecture
              <Icon name="arrow-right" size={18} className="arrow" />
            </Link>
            <Link to="/features" className="btn btn--ghost">
              See all features
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          EXAMPLE USE CASE
          ============================================================ */}
      <section className="section section--dark usecase">
        <div className="container usecase__inner">
          <div className="usecase__story reveal">
            <span className="eyebrow">Example use case</span>
            <h2>A landslide in the monsoon, with no signal.</h2>
            <p>
              A trekking group is caught in a landslide during heavy rainfall. The telecom tower
              serving the area is damaged. Mobile phones show "No Service." The internet is
              unavailable. Even emergency numbers can't connect.
            </p>
            <p>
              One person presses the SETU SOS button. The device transmits a packet over LoRa. A
              nearby SETU gateway picks up the signal. The alert appears on the emergency
              dashboard with location, device ID, and timestamp. A responder acknowledges, the
              incident is assigned, and a response team is dispatched.
            </p>
            <p className="usecase__quote">
              "It provides a communication bridge at the moment when conventional communication
              may fail."
            </p>
          </div>

          <ol className="usecase__steps reveal reveal-delay-2">
            <li>
              <span className="usecase__num">01</span>
              <div>
                <h4>Press the SOS</h4>
                <p>One physical interaction. No app, no number, no signal needed.</p>
              </div>
            </li>
            <li>
              <span className="usecase__num">02</span>
              <div>
                <h4>Transmit over LoRa</h4>
                <p>Device ID, GPS, status, and timestamp are sent over a long-range radio link.</p>
              </div>
            </li>
            <li>
              <span className="usecase__num">03</span>
              <div>
                <h4>Gateway forwards</h4>
                <p>The local Setu gateway bridges the message to the internet when backhaul is up.</p>
              </div>
            </li>
            <li>
              <span className="usecase__num">04</span>
              <div>
                <h4>Dashboard alerts</h4>
                <p>Responders see the SOS on a map with location, status, and severity.</p>
              </div>
            </li>
            <li>
              <span className="usecase__num">05</span>
              <div>
                <h4>Team dispatched</h4>
                <p>Acknowledge, assign, and resolve, all from the central response interface.</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* ============================================================
          WHY LORA
          ============================================================ */}
      <section className="section section--soft">
        <div className="container">
          <div className="section__head">
            <div className="reveal">
              <span className="eyebrow">Why LoRa</span>
              <h2>Long range. Low power. No SIM card required.</h2>
            </div>
            <p className="lede reveal reveal-delay-1">
              LoRa is purpose-built for the conditions where SETU operates: long distances, sparse
              infrastructure, battery-powered endpoints, and a need for resilience over throughput.
            </p>
          </div>

          <div className="lora-grid">
            <div className="lora-card reveal">
              <Icon name="lora" size={32} />
              <h4>Long range</h4>
              <p>Cover entire valleys and trekking routes with strategically placed gateways.</p>
            </div>
            <div className="lora-card reveal reveal-delay-1">
              <Icon name="battery" size={32} />
              <h4>Low power</h4>
              <p>SOS devices run for extended periods on small batteries, even off-grid.</p>
            </div>
            <div className="lora-card reveal reveal-delay-2">
              <Icon name="broadcast" size={32} />
              <h4>Infrastructure-independent</h4>
              <p>Devices do not need a cellular connection to transmit an alert.</p>
            </div>
            <div className="lora-card reveal reveal-delay-3">
              <Icon name="sun" size={32} />
              <h4>Low cost</h4>
              <p>Affordable IoT components make community-scale deployment realistic.</p>
            </div>
            <div className="lora-card reveal reveal-delay-4">
              <Icon name="layers" size={32} />
              <h4>Scalable</h4>
              <p>One gateway can serve hundreds of devices in its coverage area.</p>
            </div>
            <div className="lora-card reveal reveal-delay-5">
              <Icon name="compass" size={32} />
              <h4>Terrain-friendly</h4>
              <p>Sub-GHz frequencies propagate well through hills, foliage, and obstacles.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PHILOSOPHY / CTA
          ============================================================ */}
      <section className="section">
        <div className="container">
          <div className="philosophy">
            <div className="philosophy__text reveal">
              <span className="eyebrow">Core philosophy</span>
              <h2>
                Connectivity should not be a privilege
                <span className="text-red"> during an emergency.</span>
              </h2>
              <p className="lede">
                Technology should adapt to the environment, not force the environment to provide
                perfect connectivity. SETU combines affordable hardware, long-range communication,
                location awareness, real-time software, and emergency-response coordination into a
                single, coherent system.
              </p>

              <div className="philosophy__principles">
                {principles.map((p) => (
                  <div key={p.title} className="principle">
                    <div className="principle__icon">
                      <Icon name={p.icon} size={20} />
                    </div>
                    <div>
                      <strong>{p.title}</strong>
                      <p>{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="philosophy__cta reveal reveal-delay-2">
              <h3>Build a resilient emergency layer for your community.</h3>
              <p>
                Municipalities, tourism operators, NGOs, and infrastructure projects can deploy
                SETU gateways and devices at the edges of cellular coverage.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn--primary">
                  Start a deployment
                </Link>
                <Link to="/impact" className="btn btn--ghost">
                  Read our impact
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}

/* ============================================================
   Hero device mock: a stylized SOS device + signal
   ============================================================ */
function DeviceMock() {
  return (
    <div className="device-mock">
      <div className="device-mock__glow" />
      <div className="device-mock__body">
        <div className="device-mock__screen">
          <div className="device-mock__row">
            <span className="device-mock__dot" />
            <span>SETU</span>
          </div>
          <div className="device-mock__status">SOS READY</div>
          <div className="device-mock__bars">
            <span /><span /><span /><span />
          </div>
        </div>
        <button className="device-mock__btn" aria-label="SOS">
          <span>SOS</span>
        </button>
        <div className="device-mock__pin">
          <Icon name="pin" size={14} />
          <span>GPS LOCK</span>
        </div>
      </div>

      <div className="device-mock__signal" aria-hidden>
        <div className="device-mock__ring device-mock__ring--1" />
        <div className="device-mock__ring device-mock__ring--2" />
        <div className="device-mock__ring device-mock__ring--3" />
      </div>

      <div className="device-mock__chip device-mock__chip--top">DEVICE ID 0x4A7F</div>
      <div className="device-mock__chip device-mock__chip--bot">LoRa 868 MHz · 14 dBm</div>
    </div>
  );
}
